import { CornerDownRight, Tag } from 'lucide-react'
import type { ReactNode } from 'react'
import { Tooltip } from '@/components/ui/Tooltip'
import type { WeekRow } from '@/lib/types'
import { cn } from '@/lib/utils'

interface RowLabelProps {
  row: WeekRow
  /** Eerste regel van een klantblok: alleen hier staat de klantnaam. */
  firstOfClient: boolean
  /** Tweede of latere activiteit op hetzelfde project en dezelfde categorie. */
  isContinuation: boolean
  /** Vervangt de klantnaam, bijvoorbeeld door een keuzemenu. */
  clientSlot?: ReactNode
  /** Vervangt de projectnaam, bijvoorbeeld door een keuzemenu. */
  projectSlot?: ReactNode
  /** Vervangt de categorienaam, bijvoorbeeld door een keuzemenu. */
  categorySlot?: ReactNode
  /** Twee regels in plaats van een: klant boven, project en categorie eronder. */
  stacked?: boolean
}

/**
 * Het scheidingsteken tussen twee delen van een regel (klant, project, categorie, en in
 * het teamdagoverzicht ook namen). Staat buiten de delen, zodat het in de bewerkbare
 * variant niet in het hovervlak van het verkeerde keuzemenu valt.
 */
export function RowSep() {
  return (
    <span aria-hidden="true" className="shrink-0 select-none text-ink-faint">
      ·
    </span>
  )
}

function FixedFeeTag() {
  return (
    <Tooltip content="Vaste prijs: deze uren worden niet los gefactureerd">
      <span className="ml-1 inline-flex shrink-0 cursor-help items-center gap-1 rounded-md bg-fill-2 px-1.5 py-0.5 text-[11px] font-medium text-ink-soft">
        <Tag className="h-3 w-3" aria-hidden="true" />
        vaste prijs
      </span>
    </Tooltip>
  )
}

/**
 * De klantnaam staat alleen op de eerste regel van een klantblok; daaronder blijft het
 * project staan zodat altijd zichtbaar is waar de uren bij horen. Een vervolgregel op
 * hetzelfde project en dezelfde categorie springt verder in en krijgt een pijl.
 */
export function RowLabel({ row, firstOfClient, isContinuation, clientSlot, projectSlot, categorySlot, stacked }: RowLabelProps) {
  const client = clientSlot ?? <span className="truncate font-medium text-ink">{row.clientName}</span>
  const project = projectSlot ?? <span className="truncate text-ink-faint">{row.projectName}</span>
  const category = categorySlot ?? (row.categoryName ? <span className="truncate text-ink-faint">{row.categoryName}</span> : null)

  if (stacked) {
    return (
      <span className={cn('block min-w-0', isContinuation && 'ml-1 border-l border-hairline pl-2.5')}>
        {firstOfClient ? (
          <span className="flex min-w-0 items-center gap-1">
            {client}
            {row.fixedFee ? <FixedFeeTag /> : null}
          </span>
        ) : null}
        <span className={cn('flex min-w-0 items-center gap-1 text-sm text-ink-faint', !firstOfClient && 'pl-2')}>
          {isContinuation ? (
            <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-ink-faint" aria-hidden="true" />
          ) : null}
          {project}
          {category ? (
            <>
              <RowSep />
              {category}
            </>
          ) : null}
          {!firstOfClient && row.fixedFee ? <FixedFeeTag /> : null}
        </span>
      </span>
    )
  }

  return (
    <span
      className={cn(
        'inline-flex min-w-0 items-center gap-1',
        isContinuation ? 'ml-2 border-l border-hairline pl-3' : !firstOfClient && 'pl-3',
      )}
    >
      {isContinuation ? (
        <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-ink-faint" aria-hidden="true" />
      ) : null}
      {firstOfClient ? (
        <>
          {client}
          <RowSep />
        </>
      ) : null}
      {project}
      {category ? (
        <>
          <RowSep />
          {category}
        </>
      ) : null}
      {row.fixedFee ? <FixedFeeTag /> : null}
    </span>
  )
}
